/* ============================================================
   storage — "these are already here", before anything uploads.

   Every stored file has its SHA-256. Before a batch starts, the
   server is asked which stored files share a SIZE with anything
   in the batch; only those few are hashed here (a same-sized
   twin is rare for video, so a batch of new clips costs nothing),
   and any whose hash matches is a true duplicate. The person then
   chooses: skip them, upload everything, or stop.
   ============================================================ */

import React, { useCallback, useRef, useState } from 'react';
import { Icon, fmtBytes } from './lib';
import { api } from './api';
import { Modal } from './pickers';
import { sha256File } from './sha256';

/**
 * Wraps an upload function with the duplicate check.
 * Returns [check, dialog]: call check(entries, target); render dialog.
 */
export function useDuplicateCheck(token, startUpload, { toast, onChanged, drives } = {}) {
  const label = (id) => (drives || []).find((d) => d.id === id)?.label || id;
  const [state, setState] = useState(null);
  const abortRef = useRef(null);

  const check = useCallback(async (entries, target) => {
    const files = entries.filter((e) => e.file && e.file.size > 0);
    if (!files.length) return startUpload(entries, target);
    let cand = {};
    try {
      cand = (await api.dupCheck(token, [...new Set(files.map((e) => e.file.size))])).candidates || {};
    } catch {
      // The check is a courtesy: a server that cannot answer it must not
      // stop an upload.
      return startUpload(entries, target);
    }
    const suspects = files.filter((e) => cand[String(e.file.size)]);
    if (!suspects.length) return startUpload(entries, target);

    const ctrl = new AbortController();
    abortRef.current = ctrl;
    const dups = [];
    try {
      for (let i = 0; i < suspects.length; i += 1) {
        const e = suspects[i];
        setState({ checking: true, done: i, total: suspects.length, name: e.file.name, frac: 0 });
        const h = await sha256File(e.file, {
          signal: ctrl.signal,
          onProgress: (frac) => setState((s) => (s?.checking ? { ...s, frac } : s)),
        });
        const match = cand[String(e.file.size)].find((c) => c.sha256 === h);
        if (match) dups.push({ entry: e, match });
      }
    } catch (err) {
      setState(null);
      if (err.name === 'AbortError' && abortRef.current?.skipCheck) return startUpload(entries, target);
      if (err.name === 'AbortError') return 0;
      return startUpload(entries, target);
    }
    if (!dups.length) { setState(null); return startUpload(entries, target); }
    setState({ checking: false, dups, entries, target });
    return 0;
  }, [token, startUpload]);

  const close = () => { abortRef.current?.abort(); setState(null); };
  const skipCheck = () => { if (abortRef.current) { abortRef.current.skipCheck = true; abortRef.current.abort(); } };

  let dialog = null;
  if (state?.checking) {
    dialog = (
      <Modal title="Checking for duplicates" onClose={close} footer={
        <>
          <button type="button" className="btn btn--ghost btn--sm" onClick={close}>Cancel upload</button>
          <button type="button" className="btn btn--sm" onClick={skipCheck}>Skip the check</button>
        </>
      }>
        <p className="st-dup-lead">
          {state.total} file{state.total === 1 ? ' is' : 's are'} the same size as something already stored.
          Comparing contents — nothing uploads until this is done.
        </p>
        <p className="meta st-dup-now" title={state.name}>{state.done + 1} of {state.total} · {state.name}</p>
        <div className="bar"><span className="fill" style={{ width: `${Math.round(((state.done + (state.frac || 0)) / state.total) * 100)}%` }}/></div>
      </Modal>
    );
  } else if (state?.dups) {
    const { dups, entries, target } = state;
    const dupSet = new Set(dups.map((d) => d.entry));
    const rest = entries.filter((e) => !dupSet.has(e));
    const restCount = rest.filter((e) => e.file && e.file.size > 0).length;
    const go = (list) => { setState(null); if (list.length) startUpload(list, target); };
    dialog = (
      <Modal wide title={`${dups.length} file${dups.length === 1 ? ' is' : 's are'} already stored`} onClose={close} footer={
        <>
          <button type="button" className="btn btn--ghost btn--sm" onClick={close}>Cancel</button>
          <button type="button" className="btn btn--ghost btn--sm" onClick={() => go(entries)}>Upload everything</button>
          <button type="button" className="btn btn--sm" onClick={() => go(rest)}>
            {restCount ? `Skip duplicates · upload ${restCount}` : 'Skip duplicates'}
          </button>
        </>
      }>
        <p className="st-dup-lead">Same contents, byte for byte, as a file that is already here:</p>
        <ul className="st-dup-list">
          {dups.map(({ entry, match }, i) => (
            <li key={i}>
              <span className="st-dup-name" title={entry.file.name}><Icon name="copy" size={13}/> {entry.file.name}</span>
              <span className="meta tnum">{fmtBytes(entry.file.size)}</span>
              {match.in_trash ? (
                <span className="meta st-dup-where" title={`${label(match.drive)} · ${match.path} · ${match.name}`}>
                  in the trash, from {match.path === '/' ? '' : match.path}/{match.name} · {label(match.drive)}{' '}
                  <button type="button" className="st-vp-link" onClick={async () => {
                    try {
                      await api.trashRestore(token, { ids: [match.trash_id] });
                      toast?.('ok', 'Restored from the trash', match.trash_kind === 'folder' ? `${match.name}, with its folder` : match.name);
                      onChanged?.();
                      // It is back, so this upload is the duplicate now: leave it out.
                      setState((s) => (s?.dups ? { ...s, restored: [...(s.restored || []), match.trash_id] } : s));
                    } catch (e) { toast?.('err', 'Could not restore it', e.message); }
                  }} disabled={(state.restored || []).includes(match.trash_id)}>
                    {(state.restored || []).includes(match.trash_id) ? 'restored' : 'restore it instead'}
                  </button>
                </span>
              ) : (
                <span className="meta st-dup-where" title={`${label(match.drive)} · ${match.path} · ${match.name}`}>
                  already at {match.path === '/' ? '' : match.path}/{match.name} · {label(match.drive)} · {match.owner_name}
                </span>
              )}
            </li>
          ))}
        </ul>
      </Modal>
    );
  }
  return [check, dialog];
}
